import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, ListGroup, Button, Spinner, Alert, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import api from '../../services/api';

const FavoritedUsersList = () => {
  const [favoritedUsers, setFavoritedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    per_page: 10,
    total_pages: 1
  });

  // Fetch favorited users
  useEffect(() => {
    const fetchFavoritedUsers = async () => {
      try {
        setLoading(true);
        const response = await api.get('/my-favorite');
        setFavoritedUsers(response.data.users || []);
        
        // Set pagination data if available
        if (response.data.pagination) {
          setPagination({
            page: response.data.pagination.page || 1,
            total: response.data.pagination.total || 0,
            per_page: response.data.pagination.per_page || 10,
            total_pages: response.data.pagination.total_pages || 1
          });
        }
      } catch (err) {
        console.error('Error fetching favorited users:', err);
        setError('Failed to load favorited users. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchFavoritedUsers();
  }, []);

  // Handle unfavoriting a user
  const handleUnfavorite = async (username) => {
    try {
      setSubmitting(true);
      await api.post(`/users/${username}/unfavorite`);
      
      // Remove the unfavorited user from the list
      setFavoritedUsers(prevUsers => prevUsers.filter(user => user.username !== username));
      
      // Show success message
      setActionSuccess(`${username} has been removed from favorites`);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setActionSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error unfavoriting user:', err);
      setError('Failed to remove user from favorites. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Render pagination controls
  const renderPagination = () => {
    if (pagination.total_pages <= 1) return null;

    const totalPages = pagination.total_pages;
    const currentPage = pagination.page;
    const items = [];

    // Previous button
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      />
    );

    // Page numbers
    for (let page = 1; page <= totalPages; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    // Next button
    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      />
    );

    return (
      <Pagination className="justify-content-center mt-4">
        {items}
      </Pagination>
    );
  };

  // Handle page change
  const handlePageChange = async (page) => {
    try {
      setLoading(true);
      const response = await api.get(`/my-favorite?page=${page}`);
      setFavoritedUsers(response.data.users || []);
      
      // Update pagination data
      if (response.data.pagination) {
        setPagination({
          page: response.data.pagination.page || 1,
          total: response.data.pagination.total || 0,
          per_page: response.data.pagination.per_page || 10,
          total_pages: response.data.pagination.total_pages || 1
        });
      }
    } catch (err) {
      console.error('Error fetching favorited users:', err);
      setError('Failed to load favorited users. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && favoritedUsers.length === 0) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading favorited users...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <h2 className="fw-bold mb-4">Favorited Users</h2>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {actionSuccess && (
        <Alert variant="success" className="mb-4">
          {actionSuccess}
        </Alert>
      )}

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {favoritedUsers.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-heart display-4 text-muted"></i>
              <p className="mt-3 text-muted">You haven't favorited any users yet</p>
            </div>
          ) : (
            <ListGroup variant="flush">
              {favoritedUsers.map(user => (
                <ListGroup.Item key={user.username} className="py-3 px-4">
                  <Row className="align-items-center">
                    <Col xs={12} md={8} className="d-flex align-items-center">
                      <div className="me-3">
                        {user.avatars?.small ? (
                          <img
                            src={user.avatars.small}
                            alt={user.username}
                            className="rounded-circle"
                            style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                          />
                        ) : (
                          <div 
                            className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                            style={{ width: '50px', height: '50px' }}
                          >
                            <i className="bi bi-person-fill text-secondary"></i>
                          </div>
                        )}
                      </div>
                      <div>
                        <h5 className="mb-0 fw-bold">{user.username}</h5>
                        <p className="text-muted mb-0">
                          {user.age && `${user.age} • `}
                          {user.city && user.region ? `${user.city}, ${user.region}` : (user.city || user.region || '')}
                        </p>
                      </div>
                    </Col>
                    <Col xs={12} md={4} className="mt-3 mt-md-0 text-md-end">
                      <Button
                        as={Link}
                        to={`/users/${user.username}`}
                        variant="outline-primary"
                        className="me-2"
                      >
                        View Profile
                      </Button>
                      <Button
                        variant="outline-danger"
                        onClick={() => handleUnfavorite(user.username)}
                        disabled={submitting}
                      >
                        <i className="bi bi-heart-fill me-1"></i>
                        Unfavorite
                      </Button>
                    </Col>
                  </Row>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Card.Body>
      </Card>

      {renderPagination()}
    </Container>
  );
};

export default FavoritedUsersList;
